<script setup lang="ts">
import { computed, inject } from 'vue'
import BbelField from '@/views/builder/components/bbel/BbelField.vue'
import BinaryReferencePicker from './BinaryReferencePicker.vue'
import type { FieldValue, BinaryReference } from '@/types'
import { BBEL_SCOPE_KEY, EMPTY_BBEL_SCOPE, type BbelScopeContext } from '@/views/builder/composables/bbel-context'
import type { ScopeDescriptor } from '@/types/bbel-types'

/**
 * ADR-034 field editor — toggles a binary-capable field between an **Expression** (a BBEL template)
 * and a **Binary** reference (upstream bytes streamed by the engine, never evaluated). The active
 * union case *is* the mode; switching resets to that kind's empty value.
 */
const props = defineProps<{
  modelValue: FieldValue
  placeholder?: string
  singleLine?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: FieldValue]
}>()

const scopeCtx = inject<BbelScopeContext | undefined>(BBEL_SCOPE_KEY, undefined)
const scope = computed<ScopeDescriptor>(() => scopeCtx?.value ?? EMPTY_BBEL_SCOPE)

const EMPTY_REFERENCE: BinaryReference = { serviceId: '', projection: 'Output', path: [] }

function setMode(mode: 'expression' | 'binary') {
  if (props.modelValue.$type === mode) return
  emit(
    'update:modelValue',
    mode === 'expression'
      ? { $type: 'expression', expression: '' }
      : { $type: 'binary', reference: { ...EMPTY_REFERENCE } },
  )
}

function updateExpression(expression: string) {
  emit('update:modelValue', { $type: 'expression', expression })
}

function updateReference(reference: BinaryReference) {
  emit('update:modelValue', { $type: 'binary', reference })
}
</script>

<template>
  <div>
    <div class="d-flex justify-content-end mb-2">
      <div class="btn-group btn-group-sm field-mode-toggle" role="group" aria-label="Value mode">
        <button
          type="button"
          class="btn btn-outline-primary"
          :class="{ active: modelValue.$type === 'expression' }"
          @click="setMode('expression')"
        >
          Expression
        </button>
        <button
          type="button"
          class="btn btn-outline-primary"
          :class="{ active: modelValue.$type === 'binary' }"
          @click="setMode('binary')"
        >
          Binary
        </button>
      </div>
    </div>

    <BbelField
      v-if="modelValue.$type === 'expression'"
      :model-value="modelValue.expression"
      :scope="scope"
      :single-line="singleLine"
      :placeholder="placeholder"
      @update:model-value="updateExpression"
    />
    <BinaryReferencePicker
      v-else
      :model-value="modelValue.reference"
      @update:model-value="updateReference"
    />
  </div>
</template>

<style scoped>
/* A quiet mode switch — it labels the field's kind, it isn't a call to action, so keep it small and
   muted (subtle secondary fill on the active side, not a primary button). */
.field-mode-toggle .btn {
  --bs-btn-padding-y: 0.1rem;
  --bs-btn-padding-x: 0.55rem;
  --bs-btn-font-size: 0.75rem;
}
</style>
